import { useMemo, useState } from "react";
import {
  Scale,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Package,
  Layers,
  type LucideIcon,
} from "lucide-react";
import { Card } from "./Card";
import { Badge } from "./Badge";
import { PriceTag } from "./PriceTag";
import { QuantityStepper } from "./QuantityStepper";
import { inferWeightKg } from "../lib/catalogExcelUtils";
import {
  activePacks,
  resolvePack,
  packView,
  type AssortmentPack,
} from "../lib/assortments";
import { cn } from "../lib/cn";

export interface ProductCardSku {
  id: number | null;
  code: string;
  name: string;
  unit?: string | null;
  category?: string | null;
  pricePaise: number;
  nipPaise?: number | null;
  weightKg?: number | null;
  mrpPaise?: number | null;
  availableQty: number;
  active: boolean;
  photoUrls?: string[] | null;
  gstPercent?: number | null;
  qpsTier1Qty?: number | null;
  qpsTier1DiscountPercent?: number | null;
  qpsTier2Qty?: number | null;
  qpsTier2DiscountPercent?: number | null;
  packs?: AssortmentPack[] | null;
}

export interface ProductCardProps {
  sku: ProductCardSku;
  qty?: number;
  getQty?: (packId?: number | null) => number;
  selectedPackId?: number | null;
  onPackChange?: (packId: number | null) => void;
  categoryIcon?: LucideIcon;
  onSelect?: () => void;
  onAdd: (packId?: number | null) => void;
  onIncrement: (packId?: number | null) => void;
  onDecrement: (packId?: number | null) => void;
  onZoom?: (photos: string[], initialIdx: number) => void;
  className?: string;
}

export function ProductCard({
  sku,
  qty: externalQty = 0,
  getQty,
  selectedPackId: controlledPackId,
  onPackChange,
  categoryIcon: FallbackIcon = Package,
  onSelect,
  onAdd,
  onIncrement,
  onDecrement,
  onZoom,
  className,
}: ProductCardProps) {
  // Assortments
  const packs = useMemo(() => activePacks(sku), [sku]);
  const defaultP = useMemo(() => resolvePack(sku), [sku]);
  const [internalPackId, setInternalPackId] = useState<number | null>(() => defaultP?.id ?? null);
  const activePackId = controlledPackId !== undefined ? controlledPackId : internalPackId;
  const currentPack = useMemo(
    () => (activePackId != null ? packs.find((p) => p.id === activePackId) ?? defaultP : defaultP),
    [packs, activePackId, defaultP]
  );

  // Effective price, MRP, weight & available stock for the chosen pack
  const view = useMemo(() => packView(sku, currentPack), [sku, currentPack]);

  const effectiveQty = getQty ? getQty(currentPack?.id ?? null) : externalQty;
  const outOfStock = view.availableQty <= 0;
  const isLowStock = !outOfStock && view.availableQty <= 20;

  // Weight & per-kg rate
  const weightKg = view.weightKg && view.weightKg > 0 ? view.weightKg : inferWeightKg(sku.name, sku.unit);
  const hasWeight = weightKg > 0;
  const pricePerKgPaise = hasWeight ? Math.round(view.pricePaise / weightKg) : null;

  // MRP and savings
  const mrpPaise = view.mrpPaise && view.mrpPaise > 0 ? view.mrpPaise : 0;
  const hasSavings = mrpPaise > view.pricePaise;
  const mrpSavingsPercent = hasSavings ? Math.round(((mrpPaise - view.pricePaise) / mrpPaise) * 100) : 0;

  // Photos
  const photos = useMemo(() => {
    return Array.isArray(sku.photoUrls) && sku.photoUrls.length > 0 ? sku.photoUrls : [];
  }, [sku.photoUrls]);

  const [activePhotoIdx, setActivePhotoIdx] = useState(0);
  const hasMultiplePhotos = photos.length > 1;
  const currentPhoto = photos[activePhotoIdx] || photos[0];

  const hasQps = Boolean(
    (sku.qpsTier1Qty && sku.qpsTier1DiscountPercent) ||
    (sku.qpsTier2Qty && sku.qpsTier2DiscountPercent)
  );

  return (
    <Card
      className={cn(
        "group relative flex flex-col overflow-hidden rounded-card border border-border bg-card shadow-card hover:shadow-card-hover transition-all duration-200 cursor-pointer hover:border-primary/50",
        className
      )}
      onClick={onSelect}
    >
      {/* Product Image Area */}
      <div
        className="relative h-32 sm:h-44 w-full bg-sunken flex items-center justify-center p-3 border-b border-border/60 overflow-hidden group/img"
        title="Click to view details"
      >
        {currentPhoto ? (
          <img
            key={currentPhoto}
            src={currentPhoto}
            alt={sku.name}
            className="h-full w-full object-contain transition-transform duration-300 group-hover/img:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="flex flex-col items-center justify-center gap-1.5 text-muted-foreground/30">
            <FallbackIcon className="h-10 w-10 sm:h-12 sm:w-12 stroke-[1.25]" />
          </div>
        )}

        {/* Carousel Prev/Next for multiple photos */}
        {hasMultiplePhotos && (
          <>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setActivePhotoIdx((prev) => (prev - 1 + photos.length) % photos.length);
              }}
              className="absolute left-1.5 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full bg-black/60 hover:bg-black/85 text-white flex items-center justify-center transition-all opacity-90 sm:opacity-0 sm:group-hover/img:opacity-100 shadow-md z-10 cursor-pointer"
              aria-label="Previous image"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setActivePhotoIdx((prev) => (prev + 1) % photos.length);
              }}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full bg-black/60 hover:bg-black/85 text-white flex items-center justify-center transition-all opacity-90 sm:opacity-0 sm:group-hover/img:opacity-100 shadow-md z-10 cursor-pointer"
              aria-label="Next image"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <span className="absolute top-2 right-2 rounded-control bg-black/70 px-1.5 py-0.5 text-[10px] font-bold text-white z-10 type-num">
              {activePhotoIdx + 1}/{photos.length}
            </span>
          </>
        )}

        {/* Savings Badge */}
        {hasSavings && !outOfStock && (
          <span className="absolute top-2 left-2 rounded-control bg-promo text-promo-foreground shadow-xs px-1.5 py-0.5 text-[10px] font-extrabold tracking-wide z-10 type-num">
            {mrpSavingsPercent}% OFF
          </span>
        )}

        {/* Pack & Weight Pill on Image */}
        <div className="absolute bottom-2 left-2 flex flex-wrap items-center gap-1 z-10">
          {view.label ? (
            <span className="rounded-control bg-primary text-primary-foreground border border-primary/20 px-1.5 py-0.5 text-[10px] font-bold flex items-center gap-1 shadow-2xs">
              <Layers className="h-3 w-3 shrink-0" />
              {view.label}
            </span>
          ) : hasWeight ? (
            <span className="rounded-control bg-background/95 backdrop-blur-xs border border-border px-1.5 py-0.5 text-[10px] font-bold text-foreground flex items-center gap-1 shadow-2xs type-num">
              <Scale className="h-3 w-3 text-primary shrink-0" />
              {weightKg} kg
            </span>
          ) : null}
          {sku.unit && !view.label && (
            <span className="rounded-control bg-background/95 backdrop-blur-xs border border-border px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">
              {sku.unit}
            </span>
          )}
        </div>

        {/* Zoom Lightbox Trigger */}
        {currentPhoto && onZoom && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onZoom(photos, activePhotoIdx);
            }}
            className="absolute bottom-2 right-2 h-6 w-6 rounded-control bg-black/60 hover:bg-black/85 text-white flex items-center justify-center transition-all opacity-80 sm:opacity-0 sm:group-hover/img:opacity-100 z-10 shadow-xs cursor-pointer"
            title="Zoom photo"
          >
            <Maximize2 className="h-3.5 w-3.5" />
          </button>
        )}

        {/* Out of Stock Overlay */}
        {outOfStock && (
          <div className="absolute inset-0 bg-background/80 backdrop-blur-[1px] flex items-center justify-center z-10">
            <Badge variant="destructive" className="text-xs font-bold px-2.5 py-1">
              Out of Stock
            </Badge>
          </div>
        )}
      </div>

      {/* Product Info + Price + Actions */}
      <div className="flex flex-1 flex-col justify-between p-2.5 sm:p-3.5 gap-2.5">
        <div className="space-y-1.5">
          {/* SKU Code & Category Tag */}
          <div className="flex items-center justify-between gap-1 text-[10px] text-muted-foreground font-mono">
            <span className="truncate">SKU: {sku.code}</span>
            {sku.category && (
              <span className="bg-sunken border border-border px-1.5 py-0.5 rounded-control text-[9px] font-medium font-sans truncate shrink-0 text-muted-foreground">
                {sku.category}
              </span>
            )}
          </div>

          <h3
            className="font-bold text-xs sm:text-sm text-foreground leading-snug line-clamp-2"
            title={sku.name}
          >
            {sku.name}
          </h3>

          {/* KG Weight, Rate per KG & Tax Badges */}
          <div className="flex flex-wrap items-center gap-1.5 pt-0.5 text-[11px]">
            {pricePerKgPaise && (
              <span className="font-semibold text-tone-success-text bg-tone-success-soft border border-tone-success-border px-1.5 py-0.5 rounded-control text-[10px] type-num">
                ₹{(pricePerKgPaise / 100).toFixed(2)} / kg
              </span>
            )}
            <span className="text-[10px] text-muted-foreground bg-sunken px-1.5 py-0.5 rounded-control border border-border/60 type-num">
              GST {sku.gstPercent ?? 5}%
            </span>
          </div>

          {/* Assortment Pills Selector (e.g. Pack of 10, 20, 100 or 1kg, 10kg, 25kg) */}
          {packs.length > 0 && (
            <div
              className="pt-1.5 space-y-1"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-[10px] font-semibold text-muted-foreground flex items-center gap-1">
                <span>Select Assortment:</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {packs.map((p) => {
                  const isSelected = p.id === currentPack?.id;
                  return (
                    <button
                      key={p.id ?? p.label}
                      type="button"
                      onClick={() => {
                        setInternalPackId(p.id ?? null);
                        onPackChange?.(p.id ?? null);
                      }}
                      className={cn(
                        "rounded-control border px-2 py-0.5 text-[10px] font-bold transition-all type-num cursor-pointer",
                        isSelected
                          ? "border-primary bg-primary text-primary-foreground shadow-2xs ring-1 ring-primary"
                          : "border-border/80 bg-sunken text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Stock Level Indicator */}
          <div className="pt-0.5">
            {outOfStock ? (
              <span className="text-[11px] text-destructive font-semibold">Out of Stock</span>
            ) : isLowStock ? (
              <span className="text-[11px] text-tone-warning-text font-semibold">
                Low Stock ({view.availableQty} {view.label ? "packs" : "left"})
              </span>
            ) : (
              <span className="text-[11px] text-muted-foreground">
                In Stock: <strong className="text-foreground type-num">{view.availableQty}</strong> {view.label ? "packs" : "units"}
              </span>
            )}
          </div>
        </div>

        <div className="mt-auto space-y-2.5 pt-2 border-t border-border/50">
          {/* Selling Price & MRP using PriceTag primitive */}
          <PriceTag
            pricePaise={view.pricePaise}
            unit={view.label ? view.label : sku.unit}
            mrpPaise={view.mrpPaise}
            align="between"
            size="md"
          />

          {/* QPS Volume Scheme Ladder Callout */}
          {hasQps && (
            <div className="flex items-center justify-between text-[10px] bg-tone-success-soft border border-tone-success-border px-2 py-1 rounded-control text-tone-success-text">
              <span className="font-semibold">QPS Scheme:</span>
              <span className="font-bold type-num">
                {sku.qpsTier1Qty && sku.qpsTier1DiscountPercent ? `${sku.qpsTier1Qty}+ (${sku.qpsTier1DiscountPercent}%)` : ""}
                {sku.qpsTier1Qty && sku.qpsTier2Qty ? " · " : ""}
                {sku.qpsTier2Qty && sku.qpsTier2DiscountPercent ? `${sku.qpsTier2Qty}+ (${sku.qpsTier2DiscountPercent}%)` : ""}
              </span>
            </div>
          )}

          {/* Add / Stepper */}
          <QuantityStepper
            qty={effectiveQty}
            availableQty={view.availableQty}
            disabled={outOfStock || sku.id === null}
            onAdd={() => onAdd(currentPack?.id ?? null)}
            onIncrement={() => onIncrement(currentPack?.id ?? null)}
            onDecrement={() => onDecrement(currentPack?.id ?? null)}
            size="md"
          />

          {effectiveQty >= 5 && (
            <span className="text-[10px] font-bold text-tone-success-text bg-tone-success-soft border border-tone-success-border px-1.5 py-0.5 rounded-control text-center block type-num">
              🎉 Bulk Tier Active ({effectiveQty >= 20 ? "5%" : "2.5%"} Off Applied)
            </span>
          )}
        </div>
      </div>
    </Card>
  );
}
